/** Context panels (design §7.1) — a V / D / R / record shown beside a step,
    filtered by its values — and the single panel a `show` step IS. Params are
    bound by criteria / prompt NAME, so the names come from the catalog. */

import ExprInput from './ExprInput'
import {
  KeyValueEditor, ObjectPicker, RouteStringPicker, TargetPicker, useParamNames,
} from './pickers'
import { headerForm, lineFormFor, useWizardDesign } from './context'
import type { WizardContextPanel } from './types'
import {
  AddBtn, Field, Grid2, RemoveBtn, SelectInput, StringList, Sub, cardStyle, emptyHintStyle, hintStyle,
} from './ui'

const KIND_TYPE = { visual: 'V', dashboard: 'D', report: 'R', record: 'T' } as const

export function PanelEditor({ panel, onChange, formNo, showLayout = true }: {
  panel: WizardContextPanel
  onChange: (p: WizardContextPanel) => void
  formNo?: string
  showLayout?: boolean
}) {
  const ctx = useWizardDesign()
  const patch = (p: Partial<WizardContextPanel>) => onChange({ ...panel, ...p })
  const objType = KIND_TYPE[panel.kind] || 'V'
  const paramNames = useParamNames(objType, panel.obj_name)
  const recordTxn = panel.kind === 'record'
    ? ctx.catalog?.transactions.find((t) => t.obj_name === panel.obj_name) || null
    : null
  const recordCols = (headerForm(recordTxn)?.columns || []).map((c) => c.db_name || c.name)
  const targets = [
    ...(headerForm(ctx.base)?.columns || []).map((c) => `header.${c.name}`),
    ...(lineFormFor(ctx.base, ctx.model, formNo)?.columns || []).map((c) => `line.${c.name}`),
  ]
  return (
    <div>
      <Grid2>
        <Field label="Shows">
          <SelectInput value={panel.kind} disabled={ctx.readOnly}
            onChange={(kind) => onChange({ kind: kind as WizardContextPanel['kind'], obj_name: '', params: {}, layout: panel.layout })}
            options={[
              { value: 'visual', label: 'A visual' }, { value: 'dashboard', label: 'A dashboard' },
              { value: 'report', label: 'A report' }, { value: 'record', label: 'A record card' },
            ]} />
        </Field>
        <Field label="Object">
          <ObjectPicker objType={objType} value={panel.obj_name} onChange={(obj_name) => patch({ obj_name })} />
        </Field>
        <Field label="Title (optional)">
          <ExprInput mode="template" value={panel.title} onChange={(title) => patch({ title: title || undefined })} formNo={formNo} />
        </Field>
        {showLayout && (
          <Field label="Layout">
            <SelectInput value={panel.layout || 'panel'} onChange={(layout) => patch({ layout: layout as WizardContextPanel['layout'] })}
              options={[
                { value: 'strip', label: 'Strip — one KPI line above the inputs' },
                { value: 'panel', label: 'Panel' },
                { value: 'collapsed', label: 'Collapsed panel' },
              ]} />
          </Field>
        )}
      </Grid2>
      {panel.kind === 'record' ? (
        <Field label="Which record (column → template)">
          <KeyValueEditor value={panel.match} onChange={(match) => patch({ match })} keyOptions={recordCols}
            formNo={formNo} keyPlaceholder="COLUMN" />
        </Field>
      ) : (
        <Field label={panel.kind === 'report' ? 'Prompt values (by prompt name)' : 'Criteria (by criteria name)'}>
          <KeyValueEditor value={panel.params} onChange={(params) => patch({ params })}
            keyOptions={paramNames || undefined} formNo={formNo} keyPlaceholder="NAME" />
          {paramNames && paramNames.length === 0 && panel.obj_name && (
            <div style={hintStyle}>This object declares no criteria.</div>
          )}
          <div style={hintStyle}>A panel whose values are not all known yet is not drawn.</div>
        </Field>
      )}
      {showLayout && (
        <Field label="Refresh when these change">
          <StringList values={panel.refresh_on} onChange={(refresh_on) => patch({ refresh_on })} suggestions={targets} />
        </Field>
      )}
      {panel.kind === 'visual' && (
        <Sub title="Tapping a row (pick row)">
          {!panel.pick_row ? (
            <AddBtn onClick={() => patch({ pick_row: { set: {} } })}>Let a row tap set values</AddBtn>
          ) : (
            <>
              <Field label="Set (wizard target → the row's column)">
                <KeyValueEditor value={panel.pick_row.set}
                  onChange={(set) => patch({ pick_row: { ...panel.pick_row!, set: set as Record<string, string> } })}
                  valueMode="text" valuePlaceholder="COLUMN"
                  keyRender={(k, setK) => <TargetPicker value={k} onChange={setK} formNo={formNo} />} />
              </Field>
              <Field label="Then">
                <RouteStringPicker value={panel.pick_row.then}
                  onChange={(then) => patch({ pick_row: { ...panel.pick_row!, then: then || undefined } })} />
              </Field>
              <RemoveBtn title="Remove pick row" onClick={() => patch({ pick_row: undefined })} />
            </>
          )}
        </Sub>
      )}
    </div>
  )
}

export default function PanelsEditor({ panels, onChange, formNo }: {
  panels: WizardContextPanel[] | undefined
  onChange: (p: WizardContextPanel[] | undefined) => void
  formNo?: string
}) {
  const ctx = useWizardDesign()
  const list = panels || []
  const set = (next: WizardContextPanel[]) => onChange(next.length ? next : undefined)
  return (
    <div>
      {list.length === 0 && <div style={emptyHintStyle}>No context panels.</div>}
      {list.map((p, i) => (
        <div key={i} style={{ ...cardStyle, padding: 8, marginBottom: 6 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
            <strong style={{ fontSize: 13 }}>{p.obj_name || 'New panel'}</strong>
            {!ctx.readOnly && <RemoveBtn onClick={() => set(list.filter((_, j) => j !== i))} />}
          </div>
          <PanelEditor panel={p} formNo={formNo} onChange={(np) => set(list.map((x, j) => (j === i ? np : x)))} />
        </div>
      ))}
      {!ctx.readOnly && (
        <AddBtn onClick={() => set([...list, { kind: 'visual', obj_name: '', params: {}, layout: 'panel' }])}>
          Add context panel
        </AddBtn>
      )}
    </div>
  )
}
